// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getBillTosDataView,
    getCurrentBillToState,
} from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTos from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTos";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo";
import loadCurrentShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTos";
import {
    getCurrentShipTosDataView,
    getCurrentShipToState,
} from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import Upload from "@insite/mobius/Icons/Upload";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ChevronDown from "@store/Library/Components/Toolkit/Icons/ChevronDown";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import PowerBolt from "@store/Library/Components/Icons/PowerBolt";
import setChangeCustomerModalIsOpen from "@store/Framework/Store/Greif/Components/ChangeCustomerModal/Handlers/ChangeCustomerIsOpen";
import constants from "@store/constants";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { focusStyles, siteWidthStyles } from "@toolkit/Themes/common/mixins";
import isArray from "lodash/isArray";
import React, { FC, useContext, useEffect, useMemo } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import HomePin from "@store/Library/Components/Icons/HomePin";
import { linkStyles } from "@store/Themes/common/mixins/link";

const mapStateToProps = (state: ApplicationState) => {
    const billTo = getCurrentBillToState(state).value;

    return {
        billTo,
        shipTo: getCurrentShipToState(state).value,
        billTosDataView: getBillTosDataView(state, {}),
        shipTosDataView: getCurrentShipTosDataView(state),
        isAuthenticated: state.context.session.isAuthenticated,
        orderUploadLink: getPageLinkByPageType(state, constants.pageType.orderUpload),
        quickOrderLink: getPageLinkByPageType(state, constants.pageType.cart),
    };
};

const mapDispatchToProps = {
    loadBillTos,
    loadCurrentShipTos,
    loadCurrentBillTo,
    loadCurrentShipTo,
    setChangeCustomerModalIsOpen,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CurrentAccountNavigationStyles {
    wrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    customerWrapper?: InjectableCss;
    customerWrapperLink?: ClickablePresentationProps;
    customerWrapperLinkIcon?: IconProps;
    customerIcon?: IconPresentationProps;
    customerContentWrapper?: InjectableCss;
    customerAddressWrapper?: InjectableCss;
    customerAddressLabel?: TypographyPresentationProps;
    customerAddressValue?: TypographyPresentationProps;
    customerAddressDivider?: InjectableCss;
    actionsWrapper?: InjectableCss;
    quickOrderLink?: LinkPresentationProps;
    orderUploadLink?: LinkPresentationProps;
    actionsDivider?: InjectableCss;
}

const customerWrapperStyles = () => css`
    display: flex;
    align-items: flex-end;
    gap: ${pxToRem(4)};
    flex: 0 1 auto;
    min-width: 0;
`;

const getDividerProps = (height: string): InjectableCss => ({
    css: css`
        flex-shrink: 0;
        width: 1px;
        height: ${height};
        background-color: ${getColor("common.borderLight")};

        &:first-child,
        &:last-child {
            display: none;
        }
    `,
});

const getActionLinkProps = (iconSrc?: IconPresentationProps["src"]): LinkPresentationProps => ({
    icon: {
        iconProps: {
            color: "primary",
            src: iconSrc,
            css: css`
                margin-right: ${pxToRem(4)};
            `,
        },
        position: "left",
    },
    css: css`
        ${linkStyles("text", "primary", false, false)}
        white-space: nowrap;
        font-weight: ${x => x.theme.design.typography.weight.medium};
    `,
});

export const styles: CurrentAccountNavigationStyles = {
    wrapper: {
        css: css`
            ${x => breakpointValues(x.theme.design.menu.tertiaryNavigation.height, "height")}
            background-color: ${getColor("menu.tertiaryNavigation")};
            color: ${getColor("menu.tertiaryNavigation.contrast")};
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    innerWrapper: {
        css: css`
            ${siteWidthStyles()}
            display: flex;
            align-items: center;
            gap: ${pxToRem(24)};
            height: inherit;
        `,
    },
    customerWrapper: {
        css: css`
            ${customerWrapperStyles()}
        `,
    },
    customerWrapperLink: {
        css: css`
            ${customerWrapperStyles()}
            ${focusStyles()}

            &:hover .icon,
            &:focus-visible .icon {
                color: ${getColor("tertiary")};
                background-color: ${getColor("common.background")};
            }
        `,
    },
    customerWrapperLinkIcon: {
        src: ChevronDown,
        color: "tertiary.light",
        size: 24,
        css: css`
            margin-left: ${pxToRem(4)};
            border-radius: 50%;
            transition: ${transitionHoverStyles("color", "background-color")};
        `,
    },
    customerIcon: {
        color: "primary",
        src: HomePin,
    },
    customerContentWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: ${pxToRem(8)};
            min-width: 0;
        `,
    },
    customerAddressWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            min-width: 0;
        `,
    },
    customerAddressLabel: {
        css: css`
            color: ${getColor("text.light")};
            font-size: ${x => x.theme.design.typography.size.tiny};
            font-weight: ${x => x.theme.design.typography.weight.medium};
            line-height: ${x => x.theme.design.typography.lineHeight.small};
            text-transform: uppercase;
        `,
    },
    customerAddressValue: {
        ellipsis: true,
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.medium};
        `,
    },
    customerAddressDivider: getDividerProps(pxToRem(34)),
    actionsWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: ${pxToRem(16)};
            flex-shrink: 0;
            margin-left: auto;
        `,
    },
    quickOrderLink: getActionLinkProps(PowerBolt),
    orderUploadLink: getActionLinkProps(Upload),
    actionsDivider: getDividerProps(pxToRem(24)),
};

const CurrentAccount: FC<Props> = ({
    billTo,
    shipTo,
    loadBillTos,
    loadCurrentShipTos,
    billTosDataView,
    shipTosDataView,
    loadCurrentBillTo,
    loadCurrentShipTo,
    isAuthenticated,
    orderUploadLink,
    quickOrderLink,
    setChangeCustomerModalIsOpen,
}) => {
    const { isEditing, isInShell } = useContext(ShellContext);

    const showChangeCustomerLink =
        (isArray(billTosDataView.value) && billTosDataView.value.length > 1) ||
        (isArray(shipTosDataView.value) && shipTosDataView.value.length > 2);

    const wrapperCss = useMemo(() => {
        return css`
            ${styles.wrapper?.css}
            ${isEditing ? "height: auto;" : null}
            ${!isEditing && isInShell ? "[data-widget] { height: 100%; }" : null}
        `;
    }, [styles, isEditing, isInShell]);

    useEffect(() => {
        if (isAuthenticated) {
            if (!billTo) {
                loadCurrentBillTo();
            }
            if (!shipTo) {
                loadCurrentShipTo();
            }
            if (!billTosDataView.isLoading && !billTosDataView.value) {
                loadBillTos();
            }
            if (!shipTosDataView.isLoading && !shipTosDataView.value) {
                loadCurrentShipTos();
            }
        }
    }, []);

    const customerInformation = isAuthenticated && billTo && shipTo && (
        <>
            <Icon {...styles.customerIcon} />
            <StyledWrapper {...styles.customerContentWrapper}>
                <StyledWrapper {...styles.customerAddressWrapper}>
                    <Typography {...styles.customerAddressLabel}>{translate("{0}:", translate("Sold To"))}</Typography>
                    <Typography
                        {...styles.customerAddressValue}
                    >{`${billTo.companyName} ${billTo.fullAddress}`}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.customerAddressDivider} aria-hidden="true" />
                <StyledWrapper {...styles.customerAddressWrapper}>
                    <Typography {...styles.customerAddressLabel}>{translate("{0}:", translate("Ship To"))}</Typography>
                    <Typography
                        {...styles.customerAddressValue}
                    >{`${shipTo.companyName} ${shipTo.fullAddress} `}</Typography>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );

    return (
        <StyledWrapper {...styles.wrapper} css={wrapperCss} data-test-selector="headerCurrentAccount">
            <StyledWrapper {...styles.innerWrapper}>
                {customerInformation &&
                    (showChangeCustomerLink ? (
                        <Clickable
                            {...styles.customerWrapperLink}
                            aria-label={translate("Change Customer")}
                            data-test-selector="headerCurrentAccount_changeAccountButton"
                            onClick={() => setChangeCustomerModalIsOpen({ isOpen: true })}
                        >
                            {customerInformation}
                            <Icon {...styles.customerWrapperLinkIcon} className="icon" />
                        </Clickable>
                    ) : (
                        <StyledWrapper {...styles.customerWrapper}>{customerInformation}</StyledWrapper>
                    ))}
                {(quickOrderLink?.url || orderUploadLink?.url) && (
                    <StyledWrapper {...styles.actionsWrapper}>
                        {quickOrderLink?.url && (
                            <Link
                                {...styles.quickOrderLink}
                                data-test-selector="headerCurrentAccount_quickOrderButton"
                                href={quickOrderLink.url}
                            >
                                {translate("Quick Order")}
                            </Link>
                        )}
                        <StyledWrapper {...styles.actionsDivider} aria-hidden="true" />
                        {orderUploadLink?.url && (
                            <Link
                                {...styles.orderUploadLink}
                                data-test-selector="headerCurrentAccount_orderUploadButton"
                                href={orderUploadLink.url}
                            >
                                {translate("Order Upload")}
                            </Link>
                        )}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const currentAccountNavigation: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CurrentAccount),
    definition: {
        group: "Header",
        icon: "link-simple",
        fieldDefinitions: [],
        displayName: "Current Account Bar",
    },
};

export default currentAccountNavigation;
